import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon, CalendarDaysIcon, FileTextIcon, ChevronRightIcon, UserIcon, ShieldCheckIcon, CreditCardIcon, BellIcon, ClipboardListIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { dxCopy, dxCards, dxCapabilities, type Bi, type DxCapabilityKey } from '../../data/digitalExperienceHero';
import { FitScale } from './FitScale';
import { WebsiteLaptop, PortalScreen, AppPhone } from './DigitalDevices';
const EASE = [0.23, 1, 0.32, 1] as const;
const CAP_ICONS: Record<DxCapabilityKey, typeof UserIcon> = {
  sso: UserIcon,
  consent: ShieldCheckIcon,
  card: CreditCardIcon,
  notify: BellIcon,
  requests: ClipboardListIcon
};
function usePick() {
  const {
    lang
  } = useLanguage();
  return (b: Bi) => lang === 'FR' ? b.fr : b.en;
}
function AtnaCard() {
  const pick = usePick();
  return <div className="w-[310px] rounded-2xl bg-white border border-[#ECEBFA] shadow-[0_18px_40px_-24px_rgba(79,70,229,0.45)] p-4 flex items-center gap-3.5" data-id="element-3276" data-mp="2xit6vf513o0n">
      <span className="w-14 h-14 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" data-id="element-3277" data-mp="2sj838rewyrhl">
        <SparklesIcon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-3278" data-mp="36ipy9oisz0ze" />
      </span>
      <span data-id="element-3279" data-mp="31l7pby4sclre">
        <span className="block font-sans text-[17px] font-semibold text-[#4F46E5]" data-id="element-3280" data-mp="31ilhdxdzg48n">{dxCards.atna.title}</span>
        <span className="block font-sans text-[13px] text-[#4F46E5] leading-snug" data-id="element-3281" data-mp="29t8lkplekeo7">{pick(dxCards.atna.line1)}</span>
        <span className="block font-sans text-[13px] text-[#4F46E5] leading-snug" data-id="element-3282" data-mp="2nl6lqax4z088">{pick(dxCards.atna.line2)}</span>
      </span>
    </div>;
}
function ActionCard({
  icon: Icon,
  title,
  body,
  width
}: {
  icon: typeof FileTextIcon;
  title: Bi;
  body: Bi;
  width: number;
}) {
  const pick = usePick();
  return <div className="rounded-2xl bg-white border border-[#EEF0F4] shadow-[0_18px_40px_-26px_rgba(18,26,58,0.35)] p-4 flex items-center gap-3.5" style={{
    width
  }} data-id="element-3283" data-mp="1q5srks4r5pv8">
      <span className="w-12 h-12 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" data-id="element-3284" data-mp="3q5o8gcgkiq99">
        <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-3285" data-mp="3v48zbl9xjxl1" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-3286" data-mp="3uaa75i8v2md">
        <span className="block font-sans text-[13.5px] font-semibold text-[#121A3A]" data-id="element-3287" data-mp="8rahfjqtybf2">{pick(title)}</span>
        <span className="block font-sans text-[11.5px] text-[#6B7180] leading-snug mt-0.5" data-id="element-3288" data-mp="379obsocnayjc">{pick(body)}</span>
      </span>
      <ChevronRightIcon className="w-4 h-4 text-[#8A90A2] shrink-0" aria-hidden="true" data-id="element-3289" data-mp="3c89c0nrwdi7s" />
    </div>;
}

// ATNA connectors: thin indigo curves from the cards into the three surfaces.
function Connectors() {
  const dots: [number, number][] = [[372, 78], [470, 210], [612, 100], [588, 236], [676, 160], [640, 246]];
  return <svg className="absolute inset-0 pointer-events-none" width={960} height={760} aria-hidden="true" data-id="element-3290" data-mp="wixx15in6021">
      <path d="M372 78 C 452 84, 482 150, 470 210" fill="none" stroke="#6D66F0" strokeWidth={1.4} data-id="element-3291" data-mp="10k1bbnl1jo15" />
      <path d="M612 100 C 610 170, 596 206, 588 236" fill="none" stroke="#8C87F2" strokeWidth={1.2} data-id="element-3292" data-mp="2gyma72ww9inm" />
      <path d="M676 160 C 650 176, 640 214, 640 246" fill="none" stroke="#8C87F2" strokeWidth={1.2} data-id="element-3293" data-mp="2c7xjvqwyarq2" />
      {dots.map(([x, y]) => <g key={`${x}-${y}`} data-id="element-3294" data-mp="279m6j67g5z33">
          <circle cx={x} cy={y} r={7} fill="#4F46E5" opacity={0.14} data-id="element-3295" data-mp="278klc8wpvw7n" />
          <circle cx={x} cy={y} r={3} fill="#4F46E5" data-id="element-3296" data-mp="22asu7h3wce4i" />
        </g>)}
    </svg>;
}
function Composition() {
  return <div className="relative w-[960px] h-[760px]" data-id="element-3297" data-mp="1x93kc4seu6i0">
      <div className="absolute left-[450px] top-[236px]" data-id="element-3298" data-mp="1t8jkgtggcos6"><PortalScreen data-id="element-3299" data-mp="1t9bieaxiuylm" /></div>
      <div className="absolute left-[30px] top-[208px]" data-id="element-3300" data-mp="1o9yjusxag337"><WebsiteLaptop data-id="element-3301" data-mp="1ob9yxow6ox9m" /></div>
      <div className="absolute left-[752px] top-[318px]" data-id="element-3302" data-mp="1oc1k05s5ncln"><AppPhone data-id="element-3303" data-mp="3nnceio1ot7i0" /></div>
      <Connectors data-id="element-3304" data-mp="3nlrn37ao64od" />
      <div className="absolute left-[62px] top-[30px]" data-id="element-3305" data-mp="3nmb0capdk4w2"><AtnaCard data-id="element-3306" data-mp="3ingd8wkdju65" /></div>
      <div className="absolute left-[470px] top-0" data-id="element-3307" data-mp="3ipaxs9rqhk9p"><ActionCard icon={CalendarDaysIcon} title={dxCards.book.title} body={dxCards.book.body} width={300} data-id="element-3308" data-mp="3im592vn0okei" /></div>
      <div className="absolute left-[676px] top-[110px]" data-id="element-3309" data-mp="2kbwyf3v5bj2q"><ActionCard icon={FileTextIcon} title={dxCards.permit.title} body={dxCards.permit.body} width={284} data-id="element-3310" data-mp="2kbn6haqjj1ue" /></div>
    </div>;
}
export function DigitalHero() {
  const pick = usePick();
  return <div className="relative max-w-[1680px] mx-auto" data-id="element-3311" data-mp="2b9xea2ou3yvk">
      <div className="grid grid-cols-1 xl:grid-cols-[40fr_60fr] gap-12 xl:gap-6 items-center" data-id="element-3312" data-mp="1wmuf7rkv5i64">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} className="relative z-10 min-w-0" data-id="element-3313" data-mp="21d16jz6rfgs2">
          <p className="font-sans text-[13px] md:text-[14px] font-medium tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-3314" data-mp="1rmzopp502ull">{pick(dxCopy.eyebrow)}</p>
          <h1 className="font-serif text-[2.6rem] sm:text-[3.4rem] 2xl:text-[4.2rem] leading-[1.04] tracking-tight mt-6" data-id="element-3315" data-mp="1doi80bdiiid6">
            <span className="block text-[#121A3A]" data-id="element-3316" data-mp="1ionr335puwjn">{pick(dxCopy.line1)}</span>
            <span className="block text-[#121A3A]" data-id="element-3317" data-mp="z19q6rpp7es8">{pick(dxCopy.line2)}</span>
            <span className="block text-[#4F46E5]" data-id="element-3318" data-mp="u388aa40dlic">{pick(dxCopy.line3)}</span>
          </h1>
          <p className="font-sans text-[17px] md:text-[19px] text-[#4A5268] leading-relaxed mt-7 max-w-[620px]" data-id="element-3319" data-mp="1908obgcu7s0z">{pick(dxCopy.body)}</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4" data-id="element-3320" data-mp="14277l22vw4so">
            <Link to="/request-demo" onClick={() => trackEvent('Demo CTA Clicked', {
            location: 'Digital Experience Hero'
          })} className="inline-flex items-center justify-center gap-3 rounded-xl bg-[#4F46E5] px-8 py-4 font-sans text-[17px] font-medium text-white shadow-[0_14px_30px_-14px_rgba(79,70,229,0.8)] transition-colors duration-150 hover:bg-[#4338CA] whitespace-nowrap" data-id="element-3321" data-mp="1hyu81eu1pd3c">
              {pick(dxCopy.primary)}
              <ArrowRightIcon className="w-5 h-5" strokeWidth={2} aria-hidden="true" data-id="element-3322" data-mp="1wpbifnonrdcd" />
            </Link>
            <a href="#fonctionnalites" onClick={e => {
            e.preventDefault();
            document.getElementById('fonctionnalites')?.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }} className="inline-flex items-center justify-center rounded-xl border border-[#E2E3EE] bg-white px-10 py-4 font-sans text-[17px] font-medium text-[#121A3A] transition-colors duration-150 hover:border-[#C9C5F5] whitespace-nowrap" data-id="element-3323" data-mp="23shyw6w9bji3">
              {pick(dxCopy.secondary)}
            </a>
          </div>
        </motion.div>

        {/* Desktop & tablet: full three-surface composition, scaled to fit */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.06,
        ease: EASE
      }} className="hidden md:block min-w-0" data-id="element-3324" data-mp="31fqjlzofooj1">
          <FitScale width={960} height={760} data-id="element-3325" data-mp="170xo00njvxr3"><Composition data-id="element-3326" data-mp="171zad1a0o6o4" /></FitScale>
        </motion.div>

        {/* Mobile: surfaces stacked instead of squeezed side by side */}
        <div className="md:hidden space-y-6" data-id="element-3327" data-mp="3fla9v016z9go">
          <FitScale width={660} height={420} data-id="element-3328" data-mp="3fkgzzq6xs3ub"><div className="pl-[40px] pt-[4px]" data-id="element-3329" data-mp="3fj5yjx0yr86d"><WebsiteLaptop data-id="element-3330" data-mp="3bj5iy924oqxq" /></div></FitScale>
          <div className="flex justify-center" data-id="element-3331" data-mp="3bk6sua7er7dv"><AppPhone data-id="element-3332" data-mp="36kkj4aqsg99t" /></div>
          <FitScale width={310} height={100} data-id="element-3333" data-mp="36ldgwm7rroft"><AtnaCard data-id="element-3334" data-mp="36m5e21h0lu87" /></FitScale>
        </div>
      </div>

      {/* Shared foundation */}
      <ul id="fonctionnalites" className="relative mt-10 xl:-mt-6 grid grid-cols-2 lg:grid-cols-5 gap-y-10 lg:gap-y-0 lg:divide-x lg:divide-[#E4E6F0] scroll-mt-32" data-id="element-3335" data-mp="745n8epy0e5c">
        {dxCapabilities.map((cap, i) => {
        const Icon = CAP_ICONS[cap.key];
        return <li key={cap.key} className={`flex flex-col items-center text-center px-4 ${i === dxCapabilities.length - 1 ? 'col-span-2 lg:col-span-1' : ''}`} data-id="element-3336" data-mp="3pbv6wj17w5xh" data-mpi={i}>
              <span className="w-16 h-16 rounded-full bg-[#F1F0FD] flex items-center justify-center" aria-hidden="true" data-id="element-3337" data-mp="2isn6wijfff86">
                <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.6} data-id="element-3338" data-mp="ht1qk6hamgk0" />
              </span>
              <h2 className="mt-4 font-sans text-[17px] font-semibold text-[#4F46E5]" data-id="element-3339" data-mp="mrlh6k6u0gip">{pick(cap.title)}</h2>
              <p className="mt-1 font-sans text-[15px] text-[#6B7180] leading-snug max-w-[220px]" data-id="element-3340" data-mp="7v44npmwovuk">{pick(cap.body)}</p>
            </li>;
      })}
      </ul>
    </div>;
}